/* eslint-disable @typescript-eslint/naming-convention */
import { computed, inject, Injector } from '@angular/core';

import {
  patchState,
  signalStoreFeature,
  withComputed,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';

import { extractAdvancedFilters } from '@data-access/advanced-filters';

import {
  BuildLinkFromValue,
  GridColumns,
  GridConfig,
  GridFeatures,
  GridViews,
  WcgAdvancedFilter,
  WcgMenuOption,
} from '@wcg/frontend-framework';

import {
  DispatchGridViews,
  GridView,
  GridViewService,
  gridViewsMapper,
  mapAdvancedFilterByGridView,
  withGridViewsEntities,
} from '@data-access/grid-views';
import { convertKeysToCamelCase } from '@shared/utils';
import {
  DEFAULT_BASE_GRID_PROPS,
  DEFAULT_GRID_SELECT_VIEW,
  DEFAULT_PAGINATION_OPTIONS,
} from '../../constants';
import { BaseGridProps, GridPageView, GridSortBy } from '../../types';
import {
  extractMatchingGridColumns,
  getSortedColumn,
  sortColumnsByOrdinal,
  updateSortedColumn,
} from '../../utils';
import { withDialogFeatures } from '@data-access/shared';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  catchError,
  forkJoin,
  map,
  Observable,
  of,
  pipe,
  switchMap,
  tap,
} from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { withSnackBarFeature } from '@data-access/shared/store/features/with-snack-bar-features.store';

/**
 * Compare two WcgAdvancedFilter objects by their filter values.
 * Returns true when the selected values (and date range bounds) are identical.
 */
function areAdvancedFiltersEqual(
  a: WcgAdvancedFilter,
  b: WcgAdvancedFilter,
): boolean {
  const allKeys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const key of allKeys) {
    const optsA = a[key];
    const optsB = b[key];
    if (!optsA && !optsB) continue;
    if (!optsA || !optsB) return false;

    const valA = optsA.value;
    const valB = optsB.value;

    if (Array.isArray(valA) && Array.isArray(valB)) {
      if (valA.length !== valB.length) return false;
      const ids = (arr: unknown[]) =>
        arr
          .map((v) =>
            typeof v === 'string'
              ? v
              : ((v as WcgMenuOption).id ?? (v as WcgMenuOption).label ?? ''),
          )
          .sort();
      const idsA = ids(valA);
      const idsB = ids(valB);
      if (idsA.some((id, i) => id !== idsB[i])) return false;
    } else if (Array.isArray(valA) || Array.isArray(valB)) {
      // One is array, other is not — only equal if both are "empty"
      const emptyA = Array.isArray(valA) ? valA.length === 0 : !valA;
      const emptyB = Array.isArray(valB) ? valB.length === 0 : !valB;
      if (!emptyA || !emptyB) return false;
    } else if ((valA ?? '') !== (valB ?? '')) {
      return false;
    }

    if ((optsA.start ?? '') !== (optsB.start ?? '')) return false;
    if ((optsA.end ?? '') !== (optsB.end ?? '')) return false;
  }
  return true;
}

export const withGridConfigFeatures = () =>
  signalStoreFeature(
    withState<BaseGridProps>(() => ({
      ...DEFAULT_BASE_GRID_PROPS,
    })),
    withProps((store) => ({
      _configInjector: inject(Injector),
      _gridViewService: inject(GridViewService),
      store,
    })),
    withDialogFeatures(),
    withGridViewsEntities(),
    withSnackBarFeature(),
    withComputed(
      ({
        config,
        columnsCopy,
        currentStateDestroyed,
        adFilters,
        baselineAdFilters,
        pageView,
        searchString,
        sort,
        isTableColumnChanged,
        canCloseDialog,
        _gridViewsEntities,
        selectedView,
      }) => ({
        $adFilters: computed(() => adFilters),
        $advancedFilters: computed(() => {
          const _adFilters = adFilters();
          if (!_adFilters) {
            return {} as WcgAdvancedFilter;
          }
          return extractAdvancedFilters(_adFilters, columnsCopy);
        }),
        $canDialogClose: computed(() => canCloseDialog()),
        $columns: computed(() => columnsCopy()),
        $config: computed(() => config()),
        $currentStateDestroyed: computed(() => currentStateDestroyed()),
        $gridTotal: computed(() => _gridViewsEntities()?.length ?? 0),
        $gridViews: computed(() => {
          const viewsEntities = _gridViewsEntities();
          if (!viewsEntities) {
            return [] as GridViews[];
          }
          return viewsEntities;
        }),
        $isTableColumnChanged: computed(() => isTableColumnChanged()),
        $hasFilterChanges: computed(() => {
          const baseline = baselineAdFilters();
          const current = adFilters();
          if (!baseline || !Object.keys(baseline).length) return false;
          if (!current || !Object.keys(current).length) return false;
          return !areAdvancedFiltersEqual(baseline, current);
        }),
        $pageView: computed(() => pageView()),
        $paginationOptions: computed(() => DEFAULT_PAGINATION_OPTIONS),
        $searchString: computed(() => searchString()),
        $selectedView: computed(() => selectedView()),
        $sort: computed(() => {
          const _sort = sort();
          if (!_sort) {
            return null;
          }
          return _sort;
        }),
      }),
    ),
    withMethods(({ _configInjector, _gridViewService, ...store }) => {
      /**
       * @GENERAL OPERATIONS
       * */
      const _updateTableChange = (isTableChanged: boolean) => {
        patchState(store, { isTableColumnChanged: isTableChanged });
      };

      const _updateSort = (cols: GridColumns) => {
        const { sortByField, sortByOrder } = getSortedColumn(cols);

        const _sort =
          sortByField && sortByOrder
            ? {
                sortByField,
                sortByOrder,
              }
            : null;

        patchState(store, { sort: _sort });
      };
      const _updateColumns = (columns: GridColumns) => {
        const cols = sortColumnsByOrdinal(columns);
        patchState(store, { columnsCopy: { ...cols } });
      };
      const _updateGridColumns = (viewId: string): void => {
        const _columns: GridColumns = extractMatchingGridColumns(
          viewId,
          store._gridViewsEntities(),
          store.config().columns,
        );
        _updateColumns({ ..._columns });
        _updateSort({ ..._columns });
      };

      const _updateSelectedView = (view: WcgMenuOption): void => {
        patchState(store, { selectedView: view });
        _updateGridColumns(view.id!);
      };

      const _updateAllFilters = (viewId: string) => {
        const gridView = store
          ._gridViewsEntities()
          .map((g) => convertKeysToCamelCase(g) as GridViews) // @TODO REMOVE THIS WHEN WE OPTIMIZE CODE TO SUPPORT CAMEL CASE AT GLOBAL LEVEL
          ?.find((g) => g.id.toLowerCase() === viewId.toLowerCase());
        if (gridView) {
          const mergedFilters = {
            ...mapAdvancedFilterByGridView(
              store.adFilters(),
              gridView.advancedFilters,
            ),
          };
          patchState(store, {
            adFilters: mergedFilters,
            baselineAdFilters: mergedFilters,
            searchString: gridView.searchString ?? '',
          });
        }
      };

      const _updateCanCloseDialog = (canClose: boolean): void => {
        patchState(store, { canCloseDialog: canClose });
      };

      /**
       * @GENERAL OPERATIONS END
       * */

      const _afterViewModalClose = (response: GridView) => {
        _updateCanCloseDialog(true);
        _updateTableChange(false);
        _updateSelectedView({
          id: response.id,
          label: response.name as string,
        });
        patchState(store, {
          baselineAdFilters: { ...store.adFilters() },
        });
      };

      /**
       * @GRIDVIEW OPERATIONS
       * */
      const _saveGridView = rxMethod<{
        view: GridView;
      }>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(({ view }) =>
            _gridViewService.saveGridView(view).pipe(
              tapResponse({
                error: (error: Error) =>
                  patchState(store, { error: error, isLoading: false }),
                finalize: () => patchState(store, { isLoading: false }),
                next: (response) => {
                  store.addGridView(gridViewsMapper(response));
                  _afterViewModalClose(response);
                },
              }),
            ),
          ),
        ),
      );

      const _updateGridView = rxMethod<{
        view: GridView;
      }>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(({ view }) =>
            _gridViewService
              .updateGridView({
                gridView: view,
                grid_type: view.grid_type,
              })
              .pipe(
                tapResponse({
                  error: (error: Error) =>
                    patchState(store, { error: error, isLoading: false }),
                  finalize: () => patchState(store, { isLoading: false }),
                  next: (response) => {
                    store.updateGridViews(gridViewsMapper(response));
                    _afterViewModalClose(response);
                  },
                }),
              ),
          ),
        ),
      );

      const _bulkUpdateGridViews = rxMethod<{
        viewConfig: DispatchGridViews;
      }>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(({ viewConfig }) =>
            _gridViewService.bulkGridViewUpdate(viewConfig).pipe(
              tapResponse({
                error: (error: Error) =>
                  patchState(store, { error: error, isLoading: false }),
                finalize: () => patchState(store, { isLoading: false }),
                next: (response) => {
                  const _views: GridViews[] = response.map((view) => {
                    return gridViewsMapper(view);
                  });

                  if (_views.every((v) => v.id !== store.selectedView().id)) {
                    _updateSelectedView(DEFAULT_GRID_SELECT_VIEW);
                  }

                  store.setGridViews(_views);
                  _updateCanCloseDialog(true);
                  _updateTableChange(false);
                },
              }),
            ),
          ),
        ),
      );

      const _applyLinkedFilters = (
        dataObj: Record<string, unknown>,
        advancedFilters: WcgAdvancedFilter,
        linkedFilterKeys: string[],
        _buildLinkFromValue: BuildLinkFromValue,
      ): Observable<WcgAdvancedFilter> => {
        const requests = linkedFilterKeys.map((key) => {
          const filter = advancedFilters[key];

          const endpoint = _buildLinkFromValue.transform(filter.link!, dataObj);

          return _gridViewService.getFilterOptions(endpoint).pipe(
            catchError(() => of([])),
            map((options) => ({
              key,
              options,
            })),
          );
        });

        return forkJoin(requests).pipe(
          map((results) => {
            const updatedFilters: WcgAdvancedFilter = { ...advancedFilters };

            results.forEach(({ key, options }) => {
              updatedFilters[key] = {
                ...updatedFilters[key],
                options,
              };
            });

            return updatedFilters;
          }),
        );
      };

      /**
       * @GRIDVIEW OPERATIONS END
       * */

      return {
        bulkUpdateGridViews: (payload: DispatchGridViews) =>
          _bulkUpdateGridViews({ viewConfig: payload }),
        loadConfigurations: (
          _config: GridConfig,
          updateColumns = true,
          dataObj: Record<string, any> = {},
          _buildLinkFromValue: BuildLinkFromValue | null = null,
        ) => {
          if (!Object.values(_config)?.length) return;

          const columns = sortColumnsByOrdinal(
            { ..._config.columns },
            updateColumns,
          );
          const gridFeatures = convertKeysToCamelCase({
            ..._config.features,
          }) as GridFeatures;

          if (updateColumns) {
            const advancedFilters = { ..._config.advancedFilters };

            patchState(store, {
              columnsCopy: { ...columns },
            });

            const linkedFilterKeys = Object.keys(advancedFilters).filter(
              (key) =>
                advancedFilters[key].link &&
                ['multi_select', 'single_select'].includes(
                  advancedFilters[key].type ?? '',
                ),
            );

            linkedFilterKeys.forEach((key) => {
              advancedFilters[key] = {
                ...advancedFilters[key],
                options: advancedFilters[key].options || [],
              };
            });

            if (
              linkedFilterKeys.length > 0 &&
              dataObj &&
              Object.keys(dataObj).length > 0 &&
              _buildLinkFromValue
            ) {
              _applyLinkedFilters(
                dataObj,
                advancedFilters,
                linkedFilterKeys,
                _buildLinkFromValue,
              ).subscribe((updatedFilters) => {
                patchState(store, {
                  adFilters: { ...updatedFilters },
                  baselineAdFilters: { ...updatedFilters },
                });
              });
            } else {
              patchState(store, {
                adFilters: {
                  ...advancedFilters,
                },
                baselineAdFilters: {
                  ...advancedFilters,
                },
              });
            }
          }

          patchState(store, {
            config: {
              ..._config,
              columns: { ...columns },
              features: { ...gridFeatures },
            },
          });
        },

        resetCanCloseDialog: () => _updateCanCloseDialog(false),
        saveGridView: (view: GridView) => _saveGridView({ view }),
        setSelectedView: (resetSelectView: boolean, view: WcgMenuOption) => {
          if (!resetSelectView) {
            _updateSelectedView(view);
            _updateAllFilters(view.id!);
            _updateTableChange(false);
          } else {
            _updateSelectedView(DEFAULT_GRID_SELECT_VIEW);
            patchState(store, {
              baselineAdFilters: { ...store.adFilters() },
            });
          }
        },
        syncBaselineAdFilters: () =>
          patchState(store, { baselineAdFilters: { ...store.adFilters() } }),
        updateAdvancedFilters: (_filters: WcgAdvancedFilter) =>
          patchState(store, { adFilters: { ..._filters } }),
        updateColumns: (columns: GridColumns) => _updateColumns(columns),
        updateGridView: (view: GridView) => _updateGridView({ view }),
        updatePageView: (pageView: GridPageView) =>
          patchState(store, { pageView }),
        updateSearchString: (searchString: string) =>
          patchState(store, { searchString }),
        updateSort: (sort: { dir: 'asc' | 'desc'; field: string } | null) => {
          if (!sort) {
            patchState(store, { sort: null });
            return;
          }
          const sortBy: GridSortBy = {
            sortByField: sort.field,
            sortByOrder: sort.dir,
          };
          const updatedColumns = updateSortedColumn(
            store.columnsCopy(),
            sortBy,
          );
          patchState(store, { sort: sortBy, columnsCopy: updatedColumns });
        },
        updateTableChange: (isTableColumnChanged: boolean) =>
          _updateTableChange(isTableColumnChanged),
        setDefaultGridView: () => _updateSelectedView(DEFAULT_GRID_SELECT_VIEW),
      };
    }),
  );
